import { useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { getClientContractListLink } from '~/utils/getLinks';
import {
  type CreateClientContractTermFormValues,
  createClientContractTermFormSchema,
  generateRequestBody,
} from '../logics';
import { convertToContractPlanOptions } from '../logics/convertToContractPlanOptions';
import type { CreateClientContractTermFormPresentation } from '../presentations';
import { useGetPartnerAdminsClientsMutation } from './useGetPartnerAdminsClientsMutation';
import { useGetPartnerAdminsContractPlansMutation } from './useGetPartnerAdminsContractPlansMutation';
import { usePostClientContractTerm } from './usePostClientContractTerm';

type PresentationProps = React.ComponentProps<typeof CreateClientContractTermFormPresentation>;

/**
 * 企業契約期間 新規作成フォームの状態管理とアクションを提供する
 */
export const useCreateClientContractTermForm = () => {
  const threeLetterCode = getThreeLetterCode() ?? '';
  const navigate = useNavigate();
  const { showToast } = useToast();

  const methods = useForm<CreateClientContractTermFormValues>({
    defaultValues: {
      clientId: '',
      contractPlanId: '',
      startedOn: undefined,
      endedOn: undefined,
    },
    criteriaMode: 'all',
    mode: 'onBlur',
    resolver: zodResolver(createClientContractTermFormSchema),
  });

  const selectedClientId = Number(methods.watch('clientId')) || 0;

  const { trigger, isMutating } = usePostClientContractTerm(threeLetterCode, selectedClientId, {
    onSuccess: () => {
      showToast({
        message: '企業契約期間を作成しました。',
        status: 'success',
      });
      navigate(getClientContractListLink());
    },
    onError: error => {
      if (error.response?.status === 422 && Array.isArray(error.response.data?.errors)) {
        error.response.data.errors.forEach((message: string) => {
          showToast({ message, status: 'danger' });
        });
        return;
      }
      showToast({
        message: '企業契約期間の作成に失敗しました。',
        status: 'danger',
      });
    },
  });

  /**
   * フォームを送信する
   */
  const handleSubmit: PresentationProps['onSubmit'] = methods.handleSubmit(formData => {
    trigger(generateRequestBody(formData));
  });

  /**
   * キャンセルして企業契約期間一覧へ遷移する
   */
  const handleCancel: PresentationProps['onCancel'] = () => {
    navigate(getClientContractListLink());
  };

  const { trigger: triggerGetClients } = useGetPartnerAdminsClientsMutation(threeLetterCode, {
    onError: () => {
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
    },
  });

  /**
   * 利用企業選択の検索・ページネーション対応関数
   *
   * useMutation（任意発火）ベースで企業名の部分一致検索と無限スクロール（ページネーション）を行う。
   */
  const handleSearchClients: PresentationProps['searchAction'] = async (input, page) => {
    const res = await triggerGetClients({ name: input || undefined, page });
    if (!res) {
      return { options: [], headers: { 'total-pages': '0' } };
    }
    return {
      options: (res.data.clients ?? []).map(client => ({
        label: client.name,
        value: String(client.id),
      })),
      headers: { 'total-pages': String(res.headers['total-pages'] ?? '0') },
    };
  };

  const { trigger: triggerGetContractPlans } = useGetPartnerAdminsContractPlansMutation(
    threeLetterCode,
    {
      onError: () => {
        showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
      },
    }
  );

  /**
   * 契約プラン選択の検索・ページネーション対応関数
   *
   * useMutation（任意発火）ベースで契約プラン名の部分一致検索と無限スクロール（ページネーション）を行う。
   * convertToContractPlanOptions で廃止済みプラン（discontinued_at !== 0）を除外する。
   *
   * 注意: total-pages ヘッダーは廃止プランを含む全件ベースの値であるため、
   * クライアント側フィルタ後の表示件数とページ数にずれが生じる場合がある。
   */
  const handleSearchContractPlans: PresentationProps['contractPlanSearchAction'] = async (
    input,
    page
  ) => {
    const res = await triggerGetContractPlans({ name: input || undefined, page });
    if (!res) {
      return { options: [], headers: { 'total-pages': '0' } };
    }
    return {
      options: convertToContractPlanOptions(res.data),
      headers: { 'total-pages': String(res.headers['total-pages'] ?? '0') },
    };
  };

  return {
    state: { methods, isMutating, handleSearchClients, handleSearchContractPlans },
    actions: { handleSubmit, handleCancel },
  };
};
